import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Challenge, deleteChallenge } from "@/services/challengeService";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Calendar, Trophy, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditChallengeDialog } from "@/components/edit-challenge-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

interface ChallengeListProps {
  challenges: Challenge[];
  onSelect: (challenge: Challenge) => void;
  selectedId: string | undefined;
  isLoading: boolean;
  isAdmin: boolean;
  onChallengeUpdate?: () => void;
}

export function ChallengeList({ 
  challenges, 
  onSelect, 
  selectedId, 
  isLoading,
  isAdmin,
  onChallengeUpdate 
}: ChallengeListProps) {
  const [editingChallenge, setEditingChallenge] = useState<Challenge | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [challengeToDelete, setChallengeToDelete] = useState<Challenge | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const handleEditClick = (e: React.MouseEvent, challenge: Challenge) => {
    e.stopPropagation();
    setEditingChallenge(challenge);
  };

  const handleEditDialogClose = () => {
    setEditingChallenge(null);
    onChallengeUpdate?.();
  };

  const handleDeleteClick = (e: React.MouseEvent, challenge: Challenge) => {
    e.stopPropagation();
    setChallengeToDelete(challenge);
    setIsDeleteDialogOpen(true);
  };

  const deleteMutation = useMutation({
    mutationFn: deleteChallenge,
    onSuccess: () => {
      toast({ title: "Challenge gelöscht", description: "Die Challenge wurde erfolgreich gelöscht." });
      queryClient.invalidateQueries({ queryKey: ['challenges'] });
      onChallengeUpdate?.();
      if (selectedId === challengeToDelete?.id) {
        // If the deleted challenge was selected, clear the selection
        // This might require a new prop or a way to call a method on the parent
      }
    },
    onError: (error) => {
      toast({ variant: "destructive", title: "Fehler", description: "Die Challenge konnte nicht gelöscht werden." });
      console.error("Error deleting challenge:", error);
    },
    onSettled: () => {
      setIsDeleteDialogOpen(false);
      setChallengeToDelete(null);
    }
  });

  const handleConfirmDelete = () => {
    if (challengeToDelete) {
      deleteMutation.mutate(challengeToDelete.id);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <Card key={i} className="animate-pulse bg-muted/50">
            <CardContent className="p-4 h-24"></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (challenges.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Trophy className="mx-auto h-12 w-12 text-muted-foreground/60 mb-2" />
          <h3 className="font-medium">Keine Challenges</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {isAdmin 
              ? "Erstelle deine erste Vertriebschallenge." 
              : "Es wurden noch keine Challenges erstellt."}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {challenges.map(challenge => {
          const isActive = new Date(challenge.startDate) <= new Date() && 
                           new Date(challenge.endDate) >= new Date();
          const isSelected = challenge.id === selectedId;
          
          return (
            <Card 
              key={challenge.id}
              className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${
                isSelected 
                  ? "border-l-primary" 
                  : isActive 
                    ? "border-l-green-500" 
                    : "border-l-gray-200"
              }`}
              onClick={() => onSelect(challenge)}
            >
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-medium line-clamp-1">{challenge.title}</h3>
                  <div className="flex items-center gap-2">
                    {isActive ? (
                      <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100">
                        Aktiv
                      </Badge>
                    ) : (
                      <Badge variant="outline">
                        {new Date(challenge.startDate) > new Date() ? "Kommend" : "Abgeschlossen"}
                      </Badge>
                    )}
                    {isAdmin && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={(e) => handleEditClick(e, challenge)}
                          title="Challenge bearbeiten"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive/90"
                          onClick={(e) => handleDeleteClick(e, challenge)}
                          title="Challenge löschen"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{challenge.description}</p>
                
                <div className="flex items-center mt-3 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3 mr-1" />
                  <span>
                    {format(new Date(challenge.startDate), "dd.MM.yyyy", { locale: de })} - {format(new Date(challenge.endDate), "dd.MM.yyyy", { locale: de })}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {editingChallenge && (
        <EditChallengeDialog
          open={!!editingChallenge}
          onClose={handleEditDialogClose}
          challenge={editingChallenge}
        />
      )}

      {challengeToDelete && (
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Challenge löschen?</AlertDialogTitle>
              <AlertDialogDescription>
                Möchtest du die Challenge "{challengeToDelete.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>Abbrechen</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmDelete}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Wird gelöscht..." : "Löschen"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
